import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { OPAQUE_PREFIX } from './types';
import { decodeOpaquePayload } from './encode';
import { fromSnapshot } from './snapshot';
import { fromConfigString } from './configString';
import { upgradeSnapshot } from './upgrade';

/**
 * Universal workspace decoder.
 *
 * Accepts any supported format and returns a MolecularWorkspace:
 * 1. Opaque string (`alchemie:ws:v1:...`) — versioned base64 snapshot
 * 2. JSON snapshot (`{"v":1,...}`) — structured JSON
 * 3. Legacy config string (`a*C,O|b*0:1:1|...`) — pipe-delimited format
 *
 * This is the recommended entry point for deserializing workspace data
 * from any source. Legacy config strings are supported for backward
 * compatibility with existing activities and integrations.
 */
export function decodeWorkspace(input: string): MolecularWorkspace {
  // 1. Try opaque versioned format
  if (input.startsWith(OPAQUE_PREFIX)) {
    const json = decodeOpaquePayload(input);
    if (json) {
      const parsed = JSON.parse(json);
      const snapshot = upgradeSnapshot(parsed);
      return fromSnapshot(snapshot);
    }
  }

  // 2. Try raw JSON snapshot (starts with {)
  if (input.trimStart().startsWith('{')) {
    try {
      const parsed = JSON.parse(input);
      if (typeof parsed.v === 'number') {
        const snapshot = upgradeSnapshot(parsed);
        return fromSnapshot(snapshot);
      }
    } catch {
      // Not valid JSON — fall through to config string
    }
  }

  // 3. Fallback: legacy config string
  return fromConfigString(input);
}
